import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import NavBar from "../../components/organisms/navbar";
import useAuthenticate from "../../hooks/useAuthenticate";
import api from "../../services/api";
import Loading from "../../components/atoms/loading";
import Input from "../../components/atoms/input";
import { Autocomplete, TextField } from "@mui/material";
import styled from "styled-components";

const Container = styled.div`
  padding: 40px 0;
`;

const Content = styled.div`
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
`;

const PageTitleContainer = styled.div`
  padding: 0 20px;
`;

const PageLocationTitle = styled.h1`
  color: rgb(25, 31, 40);
  text-transform: uppercase;
  font-size: 24px;
  width: 100%;
  font-weight: 600;
`;

const PageTitle = styled.h1`
  color: rgb(132, 140, 155);
  text-transform: uppercase;
  font-size: 14px;
  width: 100%;
  font-weight: 600;
`;

const FormContainer = styled.div`
  padding: 0 20px;
  max-width: 600px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const DangerButton = styled.button`
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #c82333;
  }
`;

interface InvalidateCheckoutFormData {
  newValueSubscription: string;
  newValueCombo?: string;
}

const InvalidateUnpayedCheckoutsPage = () => {
  const { getToken, account } = useAuthenticate();
  const [loading, setLoading] = useState(false);
  const [tournaments, setTournaments] = useState<any[]>([]);
  const [selectedTournaments, setSelectedTournaments] = useState<any[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<InvalidateCheckoutFormData>();

  useEffect(() => {
    setLoading(true);
    // Buscar torneios disponíveis
    api.get("/tournaments")
      .then(({ data }) => {
        setTournaments(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        toast.error("Não foi possível carregar os torneios.");
      });
  }, []);

  const onSubmit = async (data: any) => {
    if (!selectedTournaments || selectedTournaments.length === 0) {
      toast.error("Por favor, selecione pelo menos um torneio.");
      return;
    }

    const torneiosPayload = selectedTournaments.map((t: any, index: number) => {
      const valorInscricao = parseFloat(data.torneios[index].valorDaInscricao);
      const valorCombo = data.torneios[index].valorDoCombo
        ? parseFloat(data.torneios[index].valorDoCombo)
        : undefined;

      if (isNaN(valorInscricao) || valorInscricao <= 0) {
        toast.error(`Valor da inscrição inválido para o torneio ${t.name}`);
        throw new Error("Valor inválido");
      }

      return {
        id: t.id,
        valorDaInscricao: valorInscricao,
        valorDoCombo: valorCombo,
      };
    });

    const payload = { torneios: torneiosPayload };

    setLoading(true);
    try {
      await api.post(`/payments/invalidate-checkouts`, payload, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
      });

      toast.success("Checkouts invalidados com sucesso!");
      reset();
      setSelectedTournaments([]);
    } catch (error: any) {
      console.error("Erro ao invalidar checkouts:", error);
      toast.error(error.response?.data?.message || "Erro ao invalidar checkouts.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loading text="Aguarde, estamos processando os dados." />;
  }

  return (
    <>
      <NavBar />
      <Container>
        <Content>
          <PageTitleContainer>
            <PageLocationTitle>Invalidar Checkouts Não Pagos</PageLocationTitle>
            <PageTitle>
              Selecione um ou mais torneios e informe o novo valor para invalidar os checkouts não pagos
            </PageTitle>
          </PageTitleContainer>

          <FormContainer>
            <form onSubmit={handleSubmit(onSubmit)}>
              <FormGroup>
                <div style={{ marginBottom: "14px" }}>
                  <label style={{
                    fontFamily: "Inter, sans-serif",
                    fontStyle: "normal",
                    fontWeight: "400",
                    fontSize: "16px",
                    lineHeight: "150%",
                    color: "#33303e",
                    textAlign: "left",
                    display: "block",
                    marginBottom: "8px"
                  }}>
                    Torneios
                  </label>
                  <Autocomplete
                    multiple
                    options={tournaments}
                    getOptionLabel={(option) => option.name}
                    value={selectedTournaments}
                    onChange={(event, newValue) => {
                      setSelectedTournaments(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Digite para buscar torneios..."
                        variant="outlined"
                        size="small"
                        error={selectedTournaments.length === 0}
                        helperText={selectedTournaments.length === 0 ? "Selecione pelo menos um torneio" : ""}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white"
                          }
                        }}
                      />
                    )}
                    filterOptions={(options, { inputValue }) => {
                      return options.filter((option) =>
                        option.name.toLowerCase().includes(inputValue.toLowerCase())
                      );
                    }}
                    noOptionsText="Nenhum torneio encontrado"
                    loading={loading}
                    loadingText="Carregando torneios..."
                    sx={{
                      "& .MuiAutocomplete-paper": {
                        backgroundColor: "white"
                      }
                    }}
                  />
                </div>
              </FormGroup>

              {selectedTournaments.length > 0 && (
                <FormGroup>
                  <div
                    style={{
                      background: "#f8f9fa",
                      padding: "15px",
                      borderRadius: "8px",
                      marginBottom: "20px",
                    }}
                  >
                    <h4 style={{ margin: "0 0 10px 0", color: "#333" }}>
                      Informações dos Torneios Selecionados:
                    </h4>

                    {selectedTournaments.map((t: any, index: number) => (
                      <div key={t.id}>
                        <p style={{ fontSize: 20 }}>
                          {t.name.toUpperCase()}
                        </p>

                        <Input
                          label="Valor da Inscrição"
                          placeholder="Ex: 150.00"
                          type="text"
                          name={`torneios[${index}].valorDaInscricao`}
                          register={register}
                          required={true}
                        />

                        <Input
                          label="Valor do Combo (opcional)"
                          placeholder="Ex: 200.00"
                          type="text"
                          name={`torneios[${index}].valorDoCombo`}
                          register={register}
                          required={false}
                        />
                      </div>
                    ))}
                  </div>
                </FormGroup>
              )}

              <FormGroup>
                <div style={{
                  background: "#fff3cd",
                  padding: "15px",
                  borderRadius: "8px",
                  border: "1px solid #ffeaa7",
                  marginBottom: "10px"
                }}>
                  <h4 style={{ margin: "0 0 10px 0", color: "#856404" }}>
                    ⚠️ Atenção
                  </h4>
                  <p style={{ margin: "5px 0", color: "#856404", fontSize: "14px" }}>
                    Esta ação irá invalidar todos os checkouts não pagos dos torneios selecionados
                    e atualizar o valor da inscrição para o valor informado.
                  </p>
                  <p style={{ margin: "5px 0", color: "#856404", fontSize: "14px" }}>
                    <strong>Esta ação não pode ser desfeita.</strong>
                  </p>
                </div>
              </FormGroup>

              <FormGroup>
                <DangerButton
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Processando..." : "Invalidar Checkouts"}
                </DangerButton>
              </FormGroup>
            </form>
          </FormContainer>
        </Content>
      </Container>
    </>
  );
};

export default InvalidateUnpayedCheckoutsPage;